import { useQuery } from 'urql'
import { PAPERWORK_TEMPLATES_QUERY } from 'app/components/paperwork/graphql'
import { parsePacketForms } from 'app/lib/forms/builder'
import {
  availablePhoneFields,
  fieldsToVariables,
  groupFieldsBySource,
  PAPERWORK_LINK_FIELD,
} from 'app/lib/workflows/dataContext'
import type { SendPaperworkNodeData, WorkflowSettings } from 'app/types/workflows'
import { ChoiceSelect, Field, MessageField, TextField } from './fields'
import { fieldGroupChoices, type Panel } from './panelShared'
import { Section } from './Section'

const ALL_PACKETS = { page: 0, limit: 500 }

export function SendPaperworkPanel({
  data,
  set,
  errors,
  settings,
  availableFields,
  sample,
}: Panel<SendPaperworkNodeData> & {
  settings: WorkflowSettings
  availableFields: string[]
  sample?: Record<string, string>
}) {
  const phoneFields = availablePhoneFields(availableFields)
  const [{ data: packetData, fetching, error }] = useQuery({
    query: PAPERWORK_TEMPLATES_QUERY,
    variables: { archived: false, pagination: ALL_PACKETS },
    requestPolicy: `cache-and-network`,
  })
  const packets = (packetData?.paperworkTemplates.hits ?? [])
    .filter((packet) => parsePacketForms(packet.formsJson).length > 0)
    .map((packet) => ({ value: packet.id, label: packet.name }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const placeholder = error
    ? `Packets could not load`
    : fetching && packets.length === 0
      ? `Loading packets…`
      : packets.length === 0
        ? `No packets yet. Build one in Settings > Forms.`
        : `Choose a packet`

  return (
    <Section>
      <Field
        label="Packet"
        info="The patient gets a link to every form in this packet. Finished forms are filed to their chart."
        error={errors.paperworkTemplateId}
      >
        <ChoiceSelect
          ariaLabel="Packet"
          value={data.paperworkTemplateId ?? ``}
          choices={packets}
          placeholder={placeholder}
          invalid={!!errors.paperworkTemplateId}
          onChange={(paperworkTemplateId) =>
            set({
              paperworkTemplateId,
              packetName: packets.find((packet) => packet.value === paperworkTemplateId)?.label,
            })
          }
        />
      </Field>
      <Field label="Link lasts (days)" error={errors.expiresInDays}>
        <TextField
          type="number"
          min={1}
          aria-label="Link lasts (days)"
          invalid={!!errors.expiresInDays}
          value={data.expiresInDays}
          onChange={(e) => set({ expiresInDays: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
        />
      </Field>
      <Field
        label="Text message"
        info="Sent by text from the location's number, with the same quiet hours and opt-out rules as Send SMS."
        error={errors.message}
      >
        <MessageField
          value={data.message}
          onChange={(message) => set({ message })}
          error={errors.message}
          variables={fieldsToVariables([...availableFields, PAPERWORK_LINK_FIELD])}
          sample={sample}
          showSegments
        />
      </Field>
      {phoneFields.length > 0 && (
        <Field label="Number" error={errors.toField ?? errors.phone}>
          <ChoiceSelect
            ariaLabel="Number"
            value={data.toField ?? ``}
            groups={fieldGroupChoices(groupFieldsBySource(phoneFields))}
            placeholder="Select a number"
            invalid={!!errors.toField}
            onChange={(toField) => set({ toField })}
          />
        </Field>
      )}
      {settings.sendTimes.restrict && (
        <Field
          label="Overnight sends"
          info="A text that comes due between 8 PM and 8 AM cannot go out. Hold it for the morning, or drop it. The paperwork link is created either way."
        >
          <ChoiceSelect
            ariaLabel="Overnight sends"
            value={data.nightSend ?? `hold`}
            choices={[
              { value: `hold`, label: `Hold until the window opens` },
              { value: `skip`, label: `Skip the text` },
            ]}
            onChange={(nightSend) => set({ nightSend })}
          />
        </Field>
      )}
    </Section>
  )
}
